/**
 * @voltxampere/web — `<vxa-pane-inspector>` (Sprint 18 Paket B B6).
 *
 * Mockup zenit-platform.html SEKME 4 (INSPECTOR DETAY) tam
 * karşılığı. Seçili bileşeni page-level büyütülmüş şekilde
 * gösterir; mevcut sağ-panel `<vxa-inspector>` (compact form)
 * editing için kalır, bu sayfa "deeper view" — büyük tipografi,
 * 2×2 live measurement panel, theory bloğu.
 *
 * Layout (mockup birebir):
 *   ┌──────────────────────────────────┬───────────────┐
 *   │  caption                         │ Değer         │
 *   │  R₂ = 4.7kΩ      (72px italic)   │  [readonly]   │
 *   │  italic subline                  │               │
 *   │                                  │ Tolerans      │
 *   │  ┌────────┬────────┐             │  [chips]      │
 *   │  │ V on   │ I thru │             │               │
 *   │  ├────────┼────────┤             │ Eylem         │
 *   │  │ Power  │ Therm  │             │  [Sil]        │
 *   │  └────────┴────────┘             │               │
 *   │                                  │               │
 *   │  Theory (brass left border)      │               │
 *   │  ▎ Voltaj bölücü ilkesi          │               │
 *   └──────────────────────────────────┴───────────────┘
 *
 * Yan-panel inspector'ından farkı:
 *  - Sağ-panel: kompakt form, parametre düzenleme, formula KaTeX
 *  - Bu sayfa: büyük tipografi, ölçüm vurgusu, eylem butonları
 *
 * "Seçim yok" durumu: pop-art Roman numeral IV + "Bir bileşen seç"
 * yönlendirmesi. Sıçrama yok — Şefim simulator pane'ine geri döner.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";
import type { ComponentInstance } from "@voltxampere/schema";

import { currentCircuit } from "../store/circuit.js";
import { selectedNodes } from "../store/canvas.js";
import { channels } from "../store/channels.js";
import {
  computeComponentMeasurements,
  type ComponentMeasurements,
} from "../store/component-measurements.js";
import { setPane } from "../store/router.js";
import { currentLocale } from "../store/locale.js";
import { t } from "../i18n/index.js";
// Sprint 37-3 — HASARLI badge (Sprint 36 carry-over).
import { damagedComponentIds, isDamaged } from "../store/damage-state.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-pane-inspector")
export class VxaPaneInspector extends LitElement {
  private readonly _circuit = new SignalController(this, currentCircuit);
  private readonly _selected = new SignalController(this, selectedNodes);
  private readonly _channels = new SignalController(this, channels);
  private readonly _locale = new SignalController(this, currentLocale);
  // Sprint 37-3 — re-render HASARLI badge when damage state flips.
  private readonly _damaged = new SignalController(this, damagedComponentIds);

  static override styles = css`
    :host {
      display: block;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      background: var(--void);
      color: var(--ivory);
    }

    .wrap {
      max-width: 1280px;
      margin: 0 auto;
      padding: 60px var(--s-9) 120px;
    }

    .grid {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      gap: 80px;
    }

    /* ─────────────────── LEFT COLUMN ─────────────────── */
    .left {
      padding-right: 20px;
      border-right: 1px solid var(--ivory-wisp);
      min-width: 0;
    }

    .cap {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 20px;
    }

    .title {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 72px;
      color: var(--ivory);
      line-height: 0.95;
      letter-spacing: -0.015em;
      margin: 0 0 16px;
    }

    .title sub {
      color: var(--brass);
      font-style: italic;
      font-size: 0.6em;
    }

    .title .val {
      color: var(--brass);
      font-weight: 400;
    }

    .title .unit {
      font-size: 36px;
      font-style: italic;
      color: var(--brass-dim);
      margin-left: 8px;
    }

    .subline {
      font-size: 18px;
      color: var(--ivory-dim);
      font-style: italic;
      margin: 0 0 40px;
      max-width: 480px;
      line-height: 1.55;
    }

    /* live panel */
    .live-panel {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1px;
      background: var(--ivory-wisp);
      border: 1px solid var(--ivory-wisp);
      margin-bottom: 40px;
    }

    .live-card {
      background: var(--ink);
      /* Sprint 26 Lane 7 R5-VIS-7 — canonical 32 (was 28, 8-pt grid). */
      padding: 32px;
    }

    .live-card-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.25em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .live-card-label .pulse {
      width: 5px;
      height: 5px;
      background: var(--brass-hot);
      border-radius: 50%;
      box-shadow: 0 0 8px var(--brass-hot);
      animation: pulse 1.4s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .live-card-label .pulse {
        animation: none;
      }
    }

    @keyframes pulse {
      0%,
      100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.6;
        transform: scale(1.3);
      }
    }

    .live-card-value {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 56px;
      color: var(--ivory);
      line-height: 1;
      letter-spacing: -0.01em;
      font-variant-numeric: tabular-nums;
      margin-bottom: 4px;
    }

    .live-card-value .u {
      font-size: 24px;
      color: var(--brass);
      font-style: italic;
      margin-left: 6px;
    }

    .live-card-caption {
      font-family: var(--font-body);
      font-style: italic;
      font-size: 13px;
      color: var(--ivory-dim);
    }

    /* theory section */
    .theory {
      padding: 32px;
      background: var(--ink);
      border-left: 2px solid var(--brass);
      margin-bottom: 32px;
    }

    .theory-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 14px;
    }

    .theory-title {
      font-family: var(--font-display);
      font-size: 22px;
      color: var(--ivory);
      margin-bottom: 12px;
      letter-spacing: -0.005em;
    }

    .theory-body {
      font-size: 15px;
      line-height: 1.7;
      color: var(--ivory-dim);
    }

    .theory-body em {
      color: var(--brass);
      font-style: italic;
    }

    /* ─────────────────── RIGHT COLUMN ─────────────────── */
    .right {
      position: sticky;
      top: 24px;
      align-self: flex-start;
      min-width: 0;
    }

    .right-section {
      margin-bottom: 36px;
    }

    .right-section-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.3em;
      color: var(--brass);
      text-transform: uppercase;
      margin-bottom: 18px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .right-section-label::after {
      content: "";
      flex: 1;
      height: 1px;
      background: var(--ivory-wisp);
    }

    .field-hint {
      font-family: var(--font-body);
      font-style: italic;
      font-size: 13px;
      color: var(--ivory-dim);
      margin-bottom: 6px;
    }

    .ro-display {
      width: 100%;
      background: var(--ink);
      border: 1px solid var(--ink-3);
      color: var(--ivory);
      padding: 16px 20px;
      font-family: var(--font-display);
      font-size: 28px;
      font-weight: 400;
      letter-spacing: -0.01em;
      margin-bottom: 12px;
      box-sizing: border-box;
    }

    .params-table {
      width: 100%;
      border-collapse: collapse;
      font-family: var(--font-body);
      font-size: 14px;
    }

    .params-table tr + tr td {
      border-top: 1px solid var(--ivory-wisp);
    }

    .params-table th {
      text-align: left;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.18em;
      color: var(--ivory-dim);
      text-transform: uppercase;
      padding: 8px 0;
      font-weight: 400;
    }

    .params-table td {
      padding: 8px 0;
      color: var(--ivory);
      font-variant-numeric: tabular-nums;
    }

    .params-table td.unit {
      color: var(--brass);
      font-style: italic;
      width: 1%;
      white-space: nowrap;
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 12px 18px;
      background: transparent;
      border: 1px solid var(--ivory-wisp);
      color: var(--ivory-dim);
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      cursor: pointer;
      transition:
        border-color 180ms ease,
        color 180ms ease,
        background 180ms ease;
      width: 100%;
      justify-content: flex-start;
    }

    .btn:hover {
      border-color: var(--ivory);
      color: var(--ivory);
    }

    /* ─────────────────── EMPTY STATE ─────────────────── */
    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--s-4);
      padding: 120px var(--s-7);
      text-align: center;
    }

    .empty-numeral {
      font-family: var(--font-display);
      font-style: italic;
      font-size: 144px;
      color: var(--brass-dim);
      line-height: 1;
      letter-spacing: -0.02em;
    }

    .empty-title {
      font-family: var(--font-display);
      font-style: italic;
      font-size: 32px;
      color: var(--ivory);
    }

    .empty-hint {
      font-family: var(--font-body);
      font-style: italic;
      color: var(--ivory-dim);
      max-width: 400px;
      margin-bottom: var(--s-5);
    }

    .empty-cta {
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      padding: 14px 26px;
      border: 1px solid var(--brass);
      background: transparent;
      color: var(--brass);
      cursor: pointer;
      transition:
        background 200ms ease,
        color 200ms ease;
    }

    .empty-cta:hover {
      background: var(--brass);
      color: var(--void);
    }

    /* ─────────────────── RESPONSIVE ─────────────────── */
    @media (max-width: 1024px) {
      .grid {
        grid-template-columns: 1fr;
        gap: 48px;
      }

      .left {
        border-right: none;
        padding-right: 0;
      }

      .right {
        position: static;
      }
    }

    @media (max-width: 640px) {
      .wrap {
        padding: 40px var(--s-5) 80px;
      }

      .title {
        font-size: 48px;
      }

      .live-panel {
        grid-template-columns: 1fr;
      }

      .live-card-value {
        font-size: 40px;
      }

      .empty-numeral {
        font-size: 96px;
      }
    }

    /* Sprint 37-3 — HASARLI badge. Red background, ivory text,
     * italic, prominently visible. Sits between the page caption
     * and the value title so the user immediately sees damaged
     * state before reading numbers. */
    .vxa-inspector-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      font-family: "EB Garamond", serif;
      font-style: italic;
      font-size: 13px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      margin-bottom: 8px;
      border-radius: 2px;
    }
    .vxa-inspector-badge--damaged {
      background: #a02020;
      color: var(--ivory);
    }
    .vxa-inspector-badge--damaged span[aria-hidden] {
      font-size: 8px;
      vertical-align: middle;
      color: #ffd0c0;
    }
  `;

  override render() {
    const cmp = this.selectedComponent();
    if (cmp === null) return this.renderEmpty();
    return html`<div class="wrap">
      <div class="grid">${this.renderLeft(cmp)} ${this.renderRight(cmp)}</div>
    </div>`;
  }

  private renderEmpty() {
    return html`<div class="wrap">
      <div class="empty">
        <div class="empty-numeral" aria-hidden="true">IV</div>
        <div class="empty-title">${t("inspector.page.empty.title")}</div>
        <p class="empty-hint">${t("inspector.page.empty.hint")}</p>
        <button class="empty-cta" type="button" @click=${this._goSimulator}>
          ${t("inspector.page.empty.cta")}
        </button>
      </div>
    </div>`;
  }

  private renderLeft(cmp: ComponentInstance) {
    const measurements = computeComponentMeasurements(cmp, this._circuit.value);
    const formatted = this.formatPrimary(cmp);
    // Sprint 37-3 — HASARLI badge (Sprint 36 carry-over). Read the
    // computed damage signal so a state flip re-renders this row.
    const damaged = isDamaged(cmp.id);
    void this._damaged.value; // subscribe to invalidation
    const damagedBadge = damaged
      ? html`<div
          class="vxa-inspector-badge vxa-inspector-badge--damaged"
          data-testid="inspector-damaged-badge"
        >
          <span aria-hidden="true">●</span> ${t("inspector.damaged")}
        </div>`
      : null;
    return html`<div class="left">
      <div class="cap">${t("inspector.page.caption")}</div>
      ${damagedBadge}
      <h1 class="title">
        ${this.renderTitleLabel(cmp)} = <span class="val">${formatted.value}</span
        ><span class="unit">${formatted.unit}</span>
      </h1>
      <p class="subline">${this.subline(cmp)}</p>
      ${this.renderLivePanel(cmp, measurements)}
    </div>`;
  }

  private renderRight(cmp: ComponentInstance) {
    const formatted = this.formatPrimary(cmp);
    return html`<div class="right">
      <div class="right-section">
        <div class="right-section-label">${t("inspector.page.section.value")}</div>
        <div class="field-hint">${this.subtitleFor(cmp)}</div>
        <div class="ro-display" data-testid="ro-value">${formatted.value}${formatted.unit}</div>
      </div>
      <div class="right-section">
        <div class="right-section-label">${t("inspector.page.section.params")}</div>
        ${this.renderParamsTable(cmp)}
      </div>
      <div class="right-section">
        <div class="right-section-label">${t("inspector.page.section.actions")}</div>
        <div class="actions">
          <button class="btn" type="button" @click=${this._goSimulator}>
            ${t("inspector.page.action.editInSim")}
          </button>
        </div>
      </div>
    </div>`;
  }

  private renderLivePanel(cmp: ComponentInstance, m: ComponentMeasurements | null) {
    if (m === null) {
      return html`<div class="live-panel" data-testid="live-empty">
        <div class="live-card" style="grid-column: 1 / -1;">
          <div class="live-card-label">${t("inspector.measurements.empty")}</div>
          <div class="live-card-caption">${t("inspector.page.live.bindHint")}</div>
        </div>
      </div>`;
    }
    return html`<div class="live-panel">
      ${this.renderLiveCard(
        t("inspector.page.live.voltage"),
        m.vrms ?? null,
        "V",
        t("inspector.page.live.voltage.caption"),
      )}
      ${this.renderLiveCard(
        t("inspector.page.live.peak"),
        m.vpeak ?? null,
        "V",
        t("inspector.page.live.peak.caption"),
      )}
      ${this.renderLiveCard(
        t("inspector.page.live.peakToPeak"),
        m.vpp ?? null,
        "V",
        t("inspector.page.live.peakToPeak.caption"),
      )}
      ${this.renderLiveCard(
        t("inspector.page.live.average"),
        m.vavg ?? null,
        "V",
        t("inspector.page.live.average.caption"),
      )}
    </div>`;
  }

  private renderLiveCard(label: string, value: number | null, baseUnit: string, caption: string) {
    const formatted =
      value === null ? { value: "—", unit: baseUnit } : this.formatEng(value, baseUnit);
    return html`<div class="live-card">
      <div class="live-card-label"><span class="pulse" aria-hidden="true"></span>${label}</div>
      <div class="live-card-value">${formatted.value}<span class="u">${formatted.unit}</span></div>
      <div class="live-card-caption">${caption}</div>
    </div>`;
  }

  private renderParamsTable(cmp: ComponentInstance) {
    const entries = Object.entries(cmp.params).filter(([, v]) => typeof v === "number");
    if (entries.length === 0) {
      return html`<div class="field-hint">${t("inspector.page.params.empty")}</div>`;
    }
    return html`<table class="params-table">
      <tbody>
        ${entries.map(
          ([key, value]) =>
            html`<tr>
              <th>${key}</th>
              <td>${this.formatEng(value as number, "").value}</td>
              <td class="unit">${this.formatEng(value as number, "").unit}</td>
            </tr>`,
        )}
      </tbody>
    </table>`;
  }

  /**
   * Resolve the currently selected component (single-selection
   * mode). Multi-selection collapses to "no detail" in this page;
   * the side inspector handles bulk edits.
   */
  private selectedComponent(): ComponentInstance | null {
    const ids = this._selected.value;
    if (ids.length !== 1) return null;
    const id = ids[0];
    const circuit = this._circuit.value;
    if (!circuit || !id) return null;
    return circuit.components.find((c) => c.id === id) ?? null;
  }

  private renderTitleLabel(cmp: ComponentInstance): unknown {
    // R₁, C₁, V₁ … shape: first letter from id prefix (R, C, V, L, …)
    // and a subscripted index.
    const match = /^([A-Za-z]+)(\d+)?$/.exec(cmp.id);
    if (!match) return cmp.id;
    const alpha = match[1] ?? "";
    const num = match[2];
    if (!num) return alpha;
    return html`${alpha}<sub>${num}</sub>`;
  }

  private unitFor(cmp: ComponentInstance): string {
    switch (cmp.type) {
      case "resistor":
        return "Ω";
      case "capacitor":
        return "F";
      case "inductor":
        return "H";
      case "dc-source":
      case "voltage-source":
        return "V";
      case "ac-source":
      case "pulse-source":
        return "V";
      default:
        return "";
    }
  }

  /**
   * Returns `{ value, unit }` where unit is the SI-prefixed unit
   * (e.g. `kΩ`, `µF`). Both the title and the right-column
   * read-only field share this so the prefix isn't lost.
   */
  private formatPrimary(cmp: ComponentInstance): { value: string; unit: string } {
    const params = cmp.params as Record<string, unknown>;
    const baseUnit = this.unitFor(cmp);
    const candidates: readonly string[] = [
      "resistance",
      "capacitance",
      "inductance",
      "voltage",
      "amplitude",
    ];
    for (const key of candidates) {
      const v = params[key];
      if (typeof v === "number") {
        return this.formatEng(v, baseUnit);
      }
    }
    return { value: "—", unit: baseUnit };
  }

  private subtitleFor(cmp: ComponentInstance): string {
    return `${cmp.type} · ${cmp.id}`;
  }

  private subline(cmp: ComponentInstance): string {
    const locale = this._locale.value;
    // Rough localized one-liner per type — Sprint 19+ may swap
    // these for proper preset-aware copy.
    const map: Record<string, [string, string]> = {
      resistor: [
        "Pasif iki uçlu bileşen — gerilimi akıma çevirir, ısı olarak harcar.",
        "Passive two-terminal component — converts voltage to current, dissipates heat.",
      ],
      capacitor: [
        "Elektrik alanı içinde enerji depolar; AC için iletken, DC için yalıtkan.",
        "Stores energy in an electric field; conducts AC, blocks DC.",
      ],
      inductor: [
        "Manyetik alanı içinde enerji depolar; AC'ye direnir, DC'yi geçirir.",
        "Stores energy in a magnetic field; resists AC, passes DC.",
      ],
      diode: ["Tek yönlü iletken — kazıyıcı bir kapı.", "One-way conductor — a swinging gate."],
      "dc-source": ["Sabit gerilim kaynağı.", "Constant voltage source."],
      "ac-source": ["Sinüs dalgalı kaynak.", "Sinusoidal source."],
    };
    const tuple = map[cmp.type];
    return tuple ? (locale === "tr" ? tuple[0] : tuple[1]) : t("inspector.page.subline.generic");
  }

  /**
   * SI prefix scaling — returns { value, unit } so callers can
   * style the unit suffix separately. Coarse but readable.
   */
  private formatEng(value: number, baseUnit: string): { value: string; unit: string } {
    if (!Number.isFinite(value)) return { value: "—", unit: baseUnit };
    if (value === 0) return { value: "0", unit: baseUnit };
    const abs = Math.abs(value);
    const prefixes: readonly { mag: number; pre: string }[] = [
      { mag: 1e9, pre: "G" },
      { mag: 1e6, pre: "M" },
      { mag: 1e3, pre: "k" },
      { mag: 1, pre: "" },
      { mag: 1e-3, pre: "m" },
      { mag: 1e-6, pre: "µ" },
      { mag: 1e-9, pre: "n" },
      { mag: 1e-12, pre: "p" },
    ];
    for (const p of prefixes) {
      if (abs >= p.mag) {
        const scaled = value / p.mag;
        const display =
          Math.abs(scaled) >= 100
            ? scaled.toFixed(0)
            : Math.abs(scaled) >= 10
              ? scaled.toFixed(1)
              : scaled.toFixed(2);
        return { value: display, unit: `${p.pre}${baseUnit}` };
      }
    }
    return { value: value.toExponential(2), unit: baseUnit };
  }

  private readonly _goSimulator = (): void => {
    setPane("simulator");
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-pane-inspector": VxaPaneInspector;
  }
}
